/**
 * Question transformation utility.
 *
 * Shared utility for transforming backend simulation questions to frontend format.
 * Handles both true-false and multiple choice question types.
 *
 * @module questionTransform
 */

import { optionText, type OptionValue } from '@/lib/questionOptions';
import type {
  SimulationQuestion,
  Question,
  AnswerOption,
} from '@/types/exam-simulation';

function optionId(option: unknown, index: number): string {
  if (typeof option === 'object' && option !== null && 'key' in option) {
    const key = String((option as { key: unknown }).key ?? '').trim();
    if (/^[a-zA-Z]$/.test(key)) {
      return key.toLowerCase();
    }
  }
  return String.fromCodePoint(97 + index);
}

/**
 * Transform simulation question to frontend question format.
 *
 * Converts a backend SimulationQuestion into a frontend Question object.
 * Handles true-false questions specially (always shows "Richtig" and "Falsch")
 * and maps multiple choice options with letter labels (a, b, c, d, etc.).
 *
 * @param simQuestion - Backend simulation question
 * @param questionNumber - Sequential question number (for ID)
 * @param paragraphRef - Paragraph reference number (optional, defaults to 1 for listening module)
 * @param section - Section number this question belongs to (optional)
 * @returns Frontend question object
 *
 * @example
 * ```typescript
 * // Reading module (with paragraph reference and section)
 * const question = transformQuestion(simQuestion, 1, 2, 1);
 *
 * // Listening module (paragraphRef defaults to 1, section provided)
 * const question = transformQuestion(simQuestion, 1, undefined, 1);
 * // Or simply: transformQuestion(simQuestion, 1, 1) if section is not needed
 * ```
 */
export function transformQuestion(
  simQuestion: SimulationQuestion,
  questionNumber: number,
  paragraphRef: number = 1,
  section?: number,
): Question {
  let options: AnswerOption[];

  // Handle true-false questions specially
  if (simQuestion.type === 'true-false') {
    // For true-false questions, always show "Richtig" and "Falsch"
    // Use IDs that match database format: "true"/"True" for Richtig, "false"/"False" for Falsch
    options = [
      { id: 'true', text: 'Richtig' },
      { id: 'false', text: 'Falsch' },
    ];
  } else {
    // Regular multiple choice — supports legacy string[] and tagged option objects
    options = simQuestion.options.map((option, index) => ({
      id: optionId(option, index),
      text: optionText(option as OptionValue),
    }));
  }

  return {
    id: questionNumber,
    originalId: simQuestion.id, // Preserve original UUID from backend
    type: simQuestion.type,
    stem: simQuestion.text,
    options,
    paragraphRef,
    adaptiveState: simQuestion.adaptiveState ?? 'new',
    expectedAnswer: simQuestion.expectedAnswer,
    section,
  };
}
